import dayjs from 'dayjs';
import { alea } from 'seedrandom';
import type { PriceData } from '@fixter/model-vehicle-pricing-tables/ProductPriceData';
import type { PriceDataRow } from '../../schema';

export const MOT_MAX_PRICE = 5400;

type PriceRange = {
  readonly lowestPrice: number;
  readonly highestPrice: number;
  readonly saveAmount: number;
};

const roundPrice = (price: number): number => Math.round(price / 100) * 100;

/**
 *  Adjust price by random range depending on the month between -3% and 3%
 *  generate -30% - +10% range from adjusted price
 */
const makePriceRange = (
  price: number,
  marketPrice: number,
  range: [min: number, max: number] = [0, Number.POSITIVE_INFINITY]
): PriceRange => {
  const seedrandom = alea(`${dayjs().month()}`);
  const randomPercent = seedrandom() * 3; // up to 3%
  const adjustedPrice = price + (randomPercent * price) / 100;

  const [min, max] = range;
  const lowestPrice = Math.max(min, roundPrice(adjustedPrice - (30 * adjustedPrice) / 100));
  const highestPrice = Math.min(max, roundPrice(adjustedPrice + (10 * adjustedPrice) / 100));

  const averagePrice = roundPrice((highestPrice + lowestPrice) / 2);
  const saveAmount = roundPrice(marketPrice - averagePrice);
  return { lowestPrice, highestPrice, saveAmount };
};

export const buildPriceRangeRows = (prices: PriceData[]): PriceDataRow[] =>
  prices.map(({ price, marketPrice, sku, skuName, makeName, modelName }) => {
    const { lowestPrice, highestPrice, saveAmount } = makePriceRange(
      price,
      marketPrice,
      /**
       * do not exceed maximum legal price of an MOT which is 54.85
       */
      sku === 'mot' ? [0, MOT_MAX_PRICE] : undefined
    );
    return {
      makeName,
      modelName,
      sku,
      skuName,
      marketPrice,
      highestPrice,
      lowestPrice,
      saveAmount,
    };
  });
